import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X, Check, Globe, MessageSquare, Plus, Pencil, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { cdnService, getErrorMessage } from '@/services';
import type { CdnName } from '@/services';

export default function CdnManagement() {
  const { t } = useTranslation();
  const [cdns, setCdns] = useState<CdnName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingNotes, setEditingNotes] = useState<number | null>(null);
  const [notesValue, setNotesValue] = useState('');

  // Create/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCdn, setEditingCdn] = useState<CdnName | null>(null);
  const [formName, setFormName] = useState('');
  const [formDomain, setFormDomain] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async () => {
    try {
      const data = await cdnService.list();
      setCdns(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('cdn.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleBlacklist = async (cdn: CdnName) => {
    try {
      await cdnService.toggleBlacklist(cdn.id, !cdn.is_blacklisted);
      setCdns(prev =>
        prev.map(c => c.id === cdn.id ? { ...c, is_blacklisted: c.is_blacklisted ? 0 : 1 } : c)
      );
    } catch (error) {
      toast.error(getErrorMessage(error) || t('cdn.blacklistError'));
    }
  };

  const handleSaveNotes = async (id: number) => {
    try {
      await cdnService.updateNotes(id, notesValue);
      setCdns(prev =>
        prev.map(c => c.id === id ? { ...c, notes: notesValue } : c)
      );
      setEditingNotes(null);
      toast.success(t('cdn.notesUpdated'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('cdn.notesUpdateError'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await cdnService.delete(id);
      setCdns(prev => prev.filter(c => c.id !== id));
      setDeleteConfirm(null);
      toast.success(t('cdn.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('cdn.deleteError'));
    }
  };

  const openCreateModal = () => {
    setEditingCdn(null);
    setFormName('');
    setFormDomain('');
    setFormNotes('');
    setShowModal(true);
  };

  const openEditModal = (cdn: CdnName) => {
    setEditingCdn(cdn);
    setFormName(cdn.cdn_name);
    setFormDomain(cdn.domain || '');
    setFormNotes(cdn.notes || '');
    setShowModal(true);
  };

  const handleSaveCdn = async () => {
    if (!formName.trim()) return;
    setIsSaving(true);
    try {
      if (editingCdn) {
        const updated = await cdnService.update(editingCdn.id, {
          cdn_name: formName.trim(),
          domain: formDomain.trim() || undefined,
          notes: formNotes.trim() || undefined,
        });
        setCdns(prev => prev.map(c => c.id === editingCdn.id ? updated : c));
        toast.success(t('cdn.updated'));
      } else {
        const created = await cdnService.create({
          cdn_name: formName.trim(),
          domain: formDomain.trim() || undefined,
          notes: formNotes.trim() || undefined,
        });
        setCdns(prev => [...prev, created]);
        toast.success(t('cdn.created'));
      }
      setShowModal(false);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('cdn.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const blacklistedCount = cdns.filter(c => c.is_blacklisted).length;

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('cdn.title')}</h1>
          <p className="text-dark-400 mt-1">
            {cdns.length} {t('cdn.discoveredCount')}{blacklistedCount > 0 ? ` · ${blacklistedCount} ${t('cdn.blacklistedCount')}` : ''}
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('cdn.add')}
        </button>
      </div>

      {/* Info */}
      <div className="card !p-4">
        <p className="text-sm text-dark-400">
          {t('cdn.discoverInfo')}
        </p>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : cdns.length === 0 ? (
        <div className="card text-center py-12">
          <Globe size={48} className="mx-auto mb-3 text-dark-500 opacity-50" />
          <p className="text-dark-400">{t('cdn.noData')}</p>
          <p className="text-sm text-dark-500 mt-1">
            {t('cdn.autoDiscoverHelp')}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('common.domain')}</th>
                <th>{t('common.lastSeen')}</th>
                <th>{t('common.blacklist')}</th>
                <th>{t('common.notes')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {cdns.map((cdn) => (
                <tr
                  key={cdn.id}
                  className={cdn.is_blacklisted ? 'opacity-60' : ''}
                >
                  <td>
                    <span className={`font-mono text-sm ${cdn.is_blacklisted ? 'line-through text-dark-500' : 'text-dark-100'}`}>
                      {cdn.cdn_name}
                    </span>
                  </td>
                  <td className="text-dark-400 text-sm">{cdn.domain || '-'}</td>
                  <td className="text-dark-500 text-xs">{formatDate(cdn.last_seen_at)}</td>
                  <td>
                    <button
                      onClick={() => handleToggleBlacklist(cdn)}
                      className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                        cdn.is_blacklisted ? 'bg-error-500' : 'bg-dark-600'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                          cdn.is_blacklisted ? 'left-4' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td>
                    {editingNotes === cdn.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          className="input text-xs py-1 w-40"
                          placeholder={t('cdn.notesPlaceholder')}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveNotes(cdn.id);
                            if (e.key === 'Escape') setEditingNotes(null);
                          }}
                        />
                        <button
                          onClick={() => handleSaveNotes(cdn.id)}
                          className="p-1 rounded bg-primary-600 text-white hover:bg-primary-500"
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={() => setEditingNotes(null)}
                          className="p-1 rounded hover:bg-dark-600 text-dark-400"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingNotes(cdn.id);
                          setNotesValue(cdn.notes || '');
                        }}
                        className="text-xs text-dark-500 hover:text-dark-300 flex items-center gap-1 max-w-[150px] truncate"
                      >
                        {cdn.notes ? (
                          <span className="truncate">{cdn.notes}</span>
                        ) : (
                          <>
                            <MessageSquare size={12} />
                            {t('cdn.addNote')}
                          </>
                        )}
                      </button>
                    )}
                  </td>
                  <td>
                    {deleteConfirm === cdn.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(cdn.id)}
                          className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(cdn)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(cdn.id)}
                          className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-lg m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingCdn ? t('cdn.editTitle') : t('cdn.newTitle')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label">{t('common.name')} *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="input"
                  placeholder={t('cdn.namePlaceholder')}
                />
              </div>
              <div>
                <label className="label">{t('common.domain')}</label>
                <input
                  type="text"
                  value={formDomain}
                  onChange={(e) => setFormDomain(e.target.value)}
                  className="input"
                  placeholder={t('cdn.domainPlaceholder')}
                />
              </div>
              <div>
                <label className="label">{t('common.notes')}</label>
                <input
                  type="text"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="input"
                  placeholder={t('cdn.notesPlaceholder')}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <button
                onClick={() => setShowModal(false)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveCdn}
                disabled={!formName.trim() || isSaving}
                className="btn-primary"
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingCdn ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
